import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Clock, Eye, Download, FileText, Archive, RotateCcw, Check } from 'lucide-react';
import { Button, Modal } from '../../../components';
import { qualityService } from '../../../services';
import {
  Badge, MetaItem, Notice, fileUrl, formatDate, policyStatus, errorMessage, FieldLabel,
  inputClass, todayISO, isoPlusDays, isUploadedPolicy, policyFormatLabel, canPreviewInline,
} from './shared';

/**
 * Read a policy — any version of it.
 *
 * The notice at the top is the most important thing on this screen. Someone
 * opening a policy is usually about to act on it, and the three ways they can
 * be looking at something that is NOT the operative text — an old version, an
 * unpublished draft, an archived policy — all look identical otherwise. Each
 * gets a plain sentence saying so.
 *
 * `version` is 'current', 'draft', or a version number from the history list.
 */
const PolicyViewer = ({ policyId, initialVersion = 'current', onClose, onEdit, onChanged }) => {
  const [policy, setPolicy] = useState(null);
  const [version, setVersion] = useState(initialVersion);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await qualityService.getPolicy(policyId);
      setPolicy(response.data);
    } catch (error) {
      toast.error(errorMessage(error, 'This policy is no longer available.'));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [policyId]);
  useEffect(() => { setVersion(initialVersion); }, [initialVersion, policyId]);

  if (loading || !policy) {
    return (
      <Modal isOpen onClose={onClose} title="Policy" maxWidth="max-w-3xl">
        <p className="py-12 text-center text-[13px] text-[#64748B] dark:text-gray-400">Loading…</p>
      </Modal>
    );
  }

  const revision = resolveRevision(policy, version);
  if (!revision) {
    return (
      <Modal isOpen onClose={onClose} title="Version unavailable" maxWidth="max-w-lg">
        <Notice tone="amber">That version is no longer available.</Notice>
        <div className="flex justify-end pt-4">
          <Button variant="primary" onClick={() => setVersion('current')}>View current</Button>
        </div>
      </Modal>
    );
  }

  const isDraft = revision.state === 'draft';
  const isOld = revision.state === 'history';
  const state = policyStatus(policy);

  const notice = policy.archived
    ? 'This policy is archived. Its records remain available for reference.'
    : isOld
      ? 'You are viewing a previous version. Open the current policy before using it.'
      : isDraft
        ? 'This is an unpublished draft. Review and publish it when it is ready.'
        : policy.has_draft
          ? 'A draft revision is being prepared. This remains the current published version.'
          : '';

  const run = async (fn, successFallback) => {
    setBusy(true);
    try {
      const response = await fn();
      toast.success(response.message || successFallback);
      if (response.data) setPolicy(response.data);
      onChanged?.();
      return response;
    } catch (error) {
      toast.error(errorMessage(error));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const archive = () => {
    if (!window.confirm('Archive this policy? Its versions and any audit links stay available, and you can restore it at any time.')) return;
    run(() => qualityService.archivePolicy(policy.id), 'Policy archived.');
  };

  const discardDraft = () => {
    if (!window.confirm('Discard this draft? The published version, if any, is unchanged.')) return;
    run(async () => {
      const response = await qualityService.discardPolicyDraft(policy.id);
      // Discarding the only revision deletes the policy itself — there is
      // nothing left to show, so close rather than render an empty viewer.
      if (!response.data) onClose();
      else setVersion('current');
      return response;
    }, 'Draft discarded.');
  };

  return (
    <>
      <Modal isOpen onClose={onClose} title={revision.title} maxWidth="max-w-3xl">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1 mb-4">
          {revision.category} · {revision.owner_name}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {isDraft ? <Badge>Draft</Badge> : isOld ? <Badge>Previous version</Badge> : <Badge tone={state.tone}>{state.label}</Badge>}
          <Badge tone="blue">Version {revision.version}</Badge>
          <Badge>{policyFormatLabel(revision)}</Badge>
        </div>

        {notice && <Notice tone={isOld || isDraft ? 'amber' : 'blue'} className="mb-4">{notice}</Notice>}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 bg-[#F8FAFC] dark:bg-gray-800/40 border border-[#E3E9F3] dark:border-gray-800 rounded-xl p-4 mb-4">
          <MetaItem label="Category">{revision.category}</MetaItem>
          <MetaItem label="Owner">{revision.owner_name}</MetaItem>
          <MetaItem label="Review date">{formatDate(revision.review_date)}</MetaItem>
          <MetaItem label="Effective from">{isDraft ? 'Not published' : formatDate(revision.effective_date)}</MetaItem>
          <MetaItem label="Published">
            {isDraft ? 'Not published' : formatDate(String(revision.published_at || '').slice(0, 10))}
          </MetaItem>
          <MetaItem label="Format">{isUploadedPolicy(revision) ? revision.file_name : 'Written policy'}</MetaItem>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {policy.current && (
              <Button size="sm" variant="ghost" leftIcon={<Clock className="w-4 h-4" />} onClick={() => setHistoryOpen(true)}>
                Version history
              </Button>
            )}
            {policy.current && policy.has_draft && !isDraft && (
              <Button size="sm" variant="ghost" onClick={() => setVersion('draft')}>View draft</Button>
            )}
            {policy.current && (isDraft || isOld) && (
              <Button size="sm" variant="ghost" onClick={() => setVersion('current')}>View current</Button>
            )}
          </div>
          {!policy.archived && !isOld && (
            <Button size="sm" variant="ghost" leftIcon={<Archive className="w-4 h-4" />} onClick={archive} loading={busy}>
              Archive policy
            </Button>
          )}
        </div>

        {isUploadedPolicy(revision) ? (
          <div>
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 mb-2.5 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="break-all">{revision.file_name}</span>
                <span>· {((revision.file_size || 0) / 1024 / 1024).toFixed(2)} MB</span>
              </span>
              {/* "Open" only where the browser can genuinely open the file —
                  a PDF in a new tab. For a Word document that same link just
                  downloads it, so offering an eye icon labelled Open beside a
                  working in-page reader is the one control that will make
                  somebody think viewing is broken. The document is READ below;
                  the only thing this row offers for Word is the original file. */}
              {canPreviewInline(revision) && (
                <a
                  className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"
                  href={fileUrl(revision.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Eye className="w-4 h-4" /> Open in a new tab
                </a>
              )}
              <a
                className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"
                href={fileUrl(revision.file_url)}
                download={revision.file_name}
              >
                <Download className="w-4 h-4" /> Download the original
              </a>
            </p>

            {canPreviewInline(revision) ? (
              <>
                <iframe
                  title={`${revision.title} PDF`}
                  src={`${fileUrl(revision.file_url)}#navpanes=0&view=FitH`}
                  className="w-full h-[52vh] min-h-[320px] border border-[#E3E9F3] dark:border-gray-800 rounded-xl bg-[#F7F9FC]"
                />
                <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-2">
                  If your browser cannot show the PDF here, use Open or Download.
                </p>
              </>
            ) : (
              <WordReader policyId={policy.id} revision={revision} />
            )}
          </div>
        ) : (
          <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-[1.8] text-[#334155] dark:text-gray-300 bg-white dark:bg-[#1f242b] border border-[#E3E9F3] dark:border-gray-800 rounded-xl p-5 max-h-[52vh] overflow-auto">
            {revision.body}
          </pre>
        )}

        <div className="flex items-center justify-end gap-2.5 pt-5">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {isOld && <Button variant="primary" onClick={() => setVersion('current')}>View current</Button>}
          {!isOld && policy.archived && (
            <Button
              variant="primary"
              loading={busy}
              leftIcon={<RotateCcw className="w-4 h-4" />}
              onClick={() => run(() => qualityService.restorePolicy(policy.id), 'Policy restored.')}
            >
              Restore policy
            </Button>
          )}
          {!isOld && !policy.archived && isDraft && (
            <>
              <Button variant="ghost" onClick={discardDraft} loading={busy}>Discard draft</Button>
              <Button variant="secondary" onClick={() => onEdit(policy.id)}>Edit draft</Button>
              <Button variant="primary" onClick={() => setPublishOpen(true)}>Review &amp; publish</Button>
            </>
          )}
          {!isOld && !policy.archived && !isDraft && (
            <Button variant="primary" onClick={() => onEdit(policy.id)}>
              {policy.has_draft ? 'Continue draft' : 'Create revision'}
            </Button>
          )}
        </div>
      </Modal>

      <Modal isOpen={historyOpen} onClose={() => setHistoryOpen(false)} title="Version history" maxWidth="max-w-lg">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1 mb-4">{policy.title}</p>
        <div className="space-y-2.5">
          {policy.draft && (
            <VersionRow
              heading={`Version ${policy.draft.version} · Draft`}
              lines={[`Last saved ${formatDate(String(policy.draft.updated_at || policy.draft.created_at || '').slice(0, 10))}`]}
              onView={() => { setVersion('draft'); setHistoryOpen(false); }}
            />
          )}
          {[...(policy.current ? [policy.current] : []), ...(policy.history || [])].map((r) => (
            <VersionRow
              key={r.id}
              heading={`Version ${r.version}${r.state === 'current' ? ' · Current' : ''}`}
              lines={[
                `Published ${formatDate(String(r.published_at || '').slice(0, 10))} · ${r.published_by_name || r.owner_name}`,
                `Effective ${formatDate(r.effective_date)} · ${policyFormatLabel(r)}`,
              ]}
              onView={() => { setVersion(r.state === 'current' ? 'current' : r.version); setHistoryOpen(false); }}
            />
          ))}
          {!policy.current && !policy.history?.length && !policy.draft && (
            <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500">No published versions yet.</p>
          )}
        </div>
      </Modal>

      {publishOpen && (
        <PublishModal
          policy={policy}
          onClose={() => setPublishOpen(false)}
          onPublished={(data) => {
            setPolicy(data);
            setVersion('current');
            setPublishOpen(false);
            onChanged?.();
          }}
        />
      )}
    </>
  );
};

/**
 * Read a Word policy in the page.
 *
 * A browser cannot display a .docx, so "view" used to just download it —
 * which meant leaving the dashboard, opening Word and coming back merely to
 * check what a policy said. The API converts the document to HTML
 * (previewRevision) and it is rendered here instead.
 *
 * The HTML is rendered inside a SANDBOXED iframe, never injected into the
 * page. Two reasons, and both matter:
 *
 *   • It is untrusted. It comes from a file somebody uploaded, and anybody
 *     granted this module can upload one. `sandbox` with no `allow-scripts`
 *     means nothing in the document can execute or reach the dashboard around
 *     it, whatever the file contained.
 *   • It is a document. Isolating it stops the policy's own markup inheriting
 *     or fighting the dashboard's styles, so it reads like a document rather
 *     than like part of the page.
 *
 * Conversion is best-effort. An older .doc cannot be converted at all, and a
 * complex document may lose formatting — in both cases the download stays
 * available and the reader says so rather than presenting a partial rendering
 * as the whole policy.
 */
const WordReader = ({ policyId, revision }) => {
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    qualityService.previewRevision(policyId, revision.id)
      .then((response) => {
        if (cancelled) return;
        setState({ status: 'ready', html: response.data.html, notes: response.data.notes || [] });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ status: 'failed', message: previewFailureText(error) });
      });

    return () => { cancelled = true; };
  }, [policyId, revision.id]);

  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center border border-[#E3E9F3] dark:border-gray-800 rounded-xl bg-[#F8FAFD] dark:bg-gray-800/30 h-[52vh] min-h-[320px]">
        <p className="text-[13px] text-[#64748B] dark:text-gray-400">Preparing the document…</p>
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center text-center border border-[#E3E9F3] dark:border-gray-800 rounded-xl bg-[#F8FAFD] dark:bg-gray-800/30 py-12 px-6">
        <span className="w-12 h-12 rounded-2xl bg-[#EEF3FF] dark:bg-blue-900/20 flex items-center justify-center mb-4">
          <FileText className="w-6 h-6 text-blue-600" />
        </span>
        <strong className="text-[14px] text-[#0B1220] dark:text-gray-100 break-all">{revision.file_name}</strong>
        <p className="text-[12px] text-[#64748B] dark:text-gray-400 mt-2 max-w-md leading-relaxed">{state.message}</p>
        {/* One action, because there is only one thing that works: a browser
            cannot open a Word file, so an "Open" button here would download it
            too and just look like a second broken way to view it. */}
        <a
          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-blue-600 text-white text-[13.5px] font-semibold hover:bg-blue-700 mt-5"
          href={fileUrl(revision.file_url)}
          download={revision.file_name}
        >
          <Download className="w-4 h-4" /> Download to read it
        </a>
      </div>
    );
  }

  return (
    <>
      <iframe
        title={`${revision.title} document`}
        // No allow-scripts: the document is displayed, never executed.
        sandbox=""
        srcDoc={documentPage(state.html)}
        className="w-full h-[52vh] min-h-[320px] border border-[#E3E9F3] dark:border-gray-800 rounded-xl bg-white"
      />
      <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-2 leading-relaxed">
        Converted from Word for reading. Some formatting may differ from the original — use Download
        for the document as it was uploaded.
      </p>
      {state.notes?.length > 0 && (
        <Notice tone="amber" className="mt-2.5">
          <strong className="block mb-1.5">Parts of this document could not be shown exactly</strong>
          <ul className="list-disc pl-5 space-y-1">
            {state.notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        </Notice>
      )}
    </>
  );
};

/**
 * Why the in-page reader could not show this document, in words a care
 * manager can act on.
 *
 * Driven by the `reason` the API sends, NOT by the message string. Anything
 * between the browser and the handler can answer with its own text — a server
 * that has not been restarted since the reader was added returns the Express
 * 404 "Route not found", a proxy might return an HTML error page — and
 * printing that inside a policy dialog tells the manager nothing and looks
 * like the policy itself is broken.
 *
 * The download is offered in every one of these cases, so the manager is never
 * stuck; this only explains why they have to use it.
 */
const previewFailureText = (error) => {
  const reason = error?.response?.data?.reason;
  const known = {
    legacy_doc: 'This is an older .doc file, which cannot be shown in the page. Open or download it to read the full policy.',
    missing: 'This policy version is no longer available.',
    not_a_document: 'This version is not an uploaded document.',
    conversion_failed: 'This document could not be converted for reading in the page.',
  }[reason];

  if (known) return known;
  return 'This document cannot be shown in the page at the moment. Open or download it to read the full policy.';
};

/**
 * Wrap the converted HTML in a minimal document.
 *
 * The iframe is sandboxed and therefore has its own blank document, so it
 * needs its own styles — it inherits nothing from the dashboard. Kept plain
 * and readable on purpose: this should look like a policy, not like a web page.
 */
const documentPage = (html) => `<!doctype html>
<html><head><meta charset="utf-8"><style>
  :root { color-scheme: light; }
  body {
    margin: 0; padding: 28px 32px; background: #fff; color: #1f2937;
    font: 15px/1.75 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    overflow-wrap: anywhere;
  }
  h1, h2, h3, h4, h5, h6 { color: #0b1220; line-height: 1.35; margin: 1.6em 0 .6em; }
  h1 { font-size: 24px } h2 { font-size: 20px } h3 { font-size: 17px }
  h1:first-child, h2:first-child, h3:first-child { margin-top: 0 }
  p { margin: 0 0 1em }
  ul, ol { margin: 0 0 1em; padding-left: 1.4em }
  li { margin: .3em 0 }
  a { color: #2563eb }
  img { max-width: 100%; height: auto }
  table { border-collapse: collapse; width: 100%; margin: 0 0 1em }
  td, th { border: 1px solid #e3e9f3; padding: 8px 10px; text-align: left; vertical-align: top }
  th { background: #f8fafd }
</style></head><body>${html}</body></html>`;

const VersionRow = ({ heading, lines, onView }) => (
  <div className="flex items-start justify-between gap-4 border border-[#E3E9F3] dark:border-gray-800 rounded-xl px-4 py-3">
    <div className="min-w-0">
      <strong className="block text-[12.5px] font-bold text-[#0B1220] dark:text-gray-100">{heading}</strong>
      {lines.map((line) => (
        <p key={line} className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1">{line}</p>
      ))}
    </div>
    <Button size="sm" variant="secondary" onClick={onView}>View</Button>
  </div>
);

/**
 * Publish a draft.
 *
 * Every field here is a decision somebody has to make deliberately — the date
 * the policy came into force, the date it must be looked at again, and a
 * statement that a person read the whole thing. There is no automated
 * substitute for the last one, and the module should not pretend otherwise.
 */
const PublishModal = ({ policy, onClose, onPublished }) => {
  const draft = policy.draft;
  const [effective, setEffective] = useState(todayISO());
  const [reviewDate, setReviewDate] = useState(draft?.review_date || isoPlusDays(365));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await qualityService.publishPolicy(policy.id, {
        effective_date: effective,
        review_date: reviewDate,
        confirmed,
      });
      toast.success(response.message);
      onPublished(response.data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Review & publish" maxWidth="max-w-xl">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1">
          {draft?.title} · Version {draft?.version}
        </p>

        {error && <Notice tone="red">{error}</Notice>}

        <Notice tone="blue">
          {policy.current
            ? 'Publishing replaces the current version in the library. Previous versions remain available in the history.'
            : 'Publishing makes this the current version in the policy library.'}
        </Notice>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 bg-[#F8FAFC] dark:bg-gray-800/40 border border-[#E3E9F3] dark:border-gray-800 rounded-xl p-4">
          <MetaItem label="Owner">{draft?.owner_name}</MetaItem>
          <MetaItem label="Category">{draft?.category}</MetaItem>
          <MetaItem label="Format">{isUploadedPolicy(draft) ? `${policyFormatLabel(draft)} · ${draft.file_name}` : 'Written policy'}</MetaItem>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel required>Effective from</FieldLabel>
            <input
              type="date"
              className={inputClass}
              value={effective}
              max={todayISO()}
              onChange={(e) => setEffective(e.target.value)}
              required
            />
          </div>
          <div>
            <FieldLabel required>Next review date</FieldLabel>
            <input
              type="date"
              className={inputClass}
              value={reviewDate}
              min={isoPlusDays(1)}
              onChange={(e) => setReviewDate(e.target.value)}
              required
            />
          </div>
        </div>

        <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 leading-relaxed">
          The effective date must be today or earlier — a policy cannot be in force before it exists.
          Set a review date in the future.
        </p>

        <label className="flex items-start gap-3 text-[12.5px] text-[#334155] dark:text-gray-300 leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 accent-blue-600 shrink-0"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>I have reviewed the full policy and confirm it is approved for use in this service.</span>
        </label>

        <div className="flex items-center justify-end gap-2.5 pt-1">
          <Button variant="secondary" type="button" onClick={onClose}>Back to draft</Button>
          <Button variant="primary" type="submit" loading={saving} disabled={!confirmed} leftIcon={<Check className="w-4 h-4" />}>
            Publish policy
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/** 'current' | 'draft' | a version number → the revision to render. */
const resolveRevision = (policy, version) => {
  if (version === 'draft') return policy.draft;
  // A policy with no published version shows its draft under 'current' — there
  // is nothing else to show, and refusing would render an empty dialog.
  if (version === 'current') return policy.current || policy.draft;
  if (policy.current && String(policy.current.version) === String(version)) return policy.current;
  return (policy.history || []).find((r) => String(r.version) === String(version)) || null;
};

export default PolicyViewer;
